import { TriangleAlert, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Common/button';
import { User } from '@/types';
import userService from '@/services/userService';

interface SafeDeleteUserDialogProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onSuccess: () => void;
}

const SafeDeleteUserDialog: React.FC<SafeDeleteUserDialogProps> = ({
  open,
  user,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<1 | 2>(1);
  const [candidates, setCandidates] = useState<User[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && user) {
      setStep(1);
      setSelectedUserId('');
      setError(null);
      setLoadingCandidates(true);
      userService.getTransferCandidates(user.id)
        .then(setCandidates)
        .catch(() => setError(t('admin.users.safeDelete.error')))
        .finally(() => setLoadingCandidates(false));
    }
  }, [open, user, t]);

  if (!open || !user) return null;

  const selectedUser = candidates.find(c => c.id === selectedUserId) ?? null;

  const handleNext = () => {
    if (!selectedUserId && user.organization_id) return;
    setError(null);
    setStep(2);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await userService.safeDeleteUser(user.id, selectedUserId);
      onSuccess();
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (detail === 'Cannot delete the last active admin of an organization') {
        setError(t('admin.users.safeDelete.lastAdminError'));
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError(t('admin.users.safeDelete.error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const userName = user.name || user.email;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative rounded-xl border border-border bg-card w-full max-w-lg mx-4 shadow-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TriangleAlert className="w-5 h-5 text-red-400 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {t('admin.users.safeDelete.title')}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                {step === 1
                  ? t('admin.users.safeDelete.step1Title')
                  : t('admin.users.safeDelete.step2Title')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            disabled={submitting}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Step indicators */}
          <div className="flex gap-2">
            {[1, 2].map((s) => (
              <div
                key={s}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  step >= s ? 'bg-destructive' : 'bg-muted'
                }`}
              />
            ))}
          </div>

          {/* Error banner */}
          {error && (
            <div className="p-3 rounded-md border border-destructive/30 bg-destructive/10 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Step 1: Select transfer target */}
          {step === 1 && (
            <div>
              {!user.organization_id ? (
                <p className="text-sm text-muted-foreground">
                  {t('admin.users.safeDelete.noOrganization')}
                </p>
              ) : loadingCandidates ? (
                <p className="text-sm text-muted-foreground">
                  {t('admin.users.safeDelete.loadingCandidates')}
                </p>
              ) : candidates.length === 0 ? (
                <div className="p-3 rounded-md border border-amber-500/30 bg-amber-500/10 text-sm text-amber-600 dark:text-amber-400">
                  {t('admin.users.safeDelete.noCandidates')}
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-foreground">
                    {t('admin.users.safeDelete.step1Description', { name: userName })}
                  </p>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">{t('admin.users.safeDelete.selectUserPlaceholder')}</option>
                    {candidates.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name || c.email} ({c.email})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Confirm */}
          {step === 2 && (
            <div className="space-y-3">
              <p className="text-sm text-foreground">
                {t('admin.users.safeDelete.step2Description', {
                  name: userName,
                  email: user.email,
                  transferTo: selectedUser?.name || selectedUser?.email || '—',
                })}
              </p>
              <div className="p-3 rounded-md border border-destructive/30 bg-destructive/10 text-sm text-destructive">
                <strong>⚠️</strong>{' '}
                {t('admin.users.safeDelete.step1Description', { name: userName })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('global.cancel')}
          </Button>
          {step === 1 ? (
            <Button
              onClick={handleNext}
              disabled={!!user.organization_id && !selectedUserId && candidates.length > 0}
              variant="destructive"
            >
              {t('admin.users.safeDelete.nextButton')}
            </Button>
          ) : (
            <Button
              onClick={handleConfirm}
              disabled={submitting}
              variant="destructive"
            >
              {submitting ? '...' : t('admin.users.safeDelete.confirmButton')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SafeDeleteUserDialog;
